import { useState } from "react";
import { BtnPrimary, Card } from "../components/ui";
import { S, css } from "../theme";

function Dashboard({ user, protocol, todayLog, setActive, inventory = [] }) {
  const today = new Date().toDateString();
  const checked = protocol.filter(p => todayLog[p.id]);
  const adherence = protocol.length ? Math.round((checked.length / protocol.length) * 100) : 0;

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const todayIdx = (new Date().getDay() + 6) % 7;

  // Show subscribe banner if user has no active subscription and isn't a founding member
  const needsSubscription = user?.tier !== "founding_member" && user?.tier !== "pro" && user?.subscription_status !== "trialing";
  const [bannerDismissed, setBannerDismissed] = useState(false);

  return (
    <div style={{ padding: 40 }}>
      <div style={{ marginBottom: 32 }}>
        <p style={{ color: S.muted, fontSize: "0.82rem", marginBottom: 4 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
        <h1 style={{ ...css.fontDisplay, fontSize: "2rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", margin: 0 }}>
          Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, <em style={{ fontStyle: "italic", color: S.accent }}>{user?.name?.split(" ")[0] || "there"}</em>
        </h1>
      </div>

      {/* Subscribe nudge banner */}
      {needsSubscription && !bannerDismissed && (
        <div style={{
          background: "linear-gradient(135deg, #1e2520, #19211d)",
          border: "1px solid " + S.accent + "33",
          borderRadius: 16,
          padding: "18px 24px",
          marginBottom: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: "1.05rem", color: S.ink, marginBottom: 4 }}>
              Start your 7-day free trial
            </div>
            <p style={{ fontSize: "0.82rem", color: S.muted, margin: 0, lineHeight: 1.5, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>
              Unlock everything — peptide tracking, AI guidance, biomarkers, and more.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={() => setActive("pricing")} style={{
              background: S.accent, color: "#fff", border: "none", borderRadius: 100,
              padding: "10px 22px", fontFamily: "'Inter', sans-serif", fontWeight: 600,
              fontSize: "0.85rem", cursor: "pointer", whiteSpace: "nowrap",
              boxShadow: "0 4px 16px rgba(126,171,142,0.25)",
            }}>
              View plans
            </button>
            <button onClick={() => setBannerDismissed(true)} style={{
              background: "none", border: "none", color: S.muted, cursor: "pointer",
              fontSize: "1.1rem", padding: "0 4px", lineHeight: 1,
            }}>×</button>
          </div>
        </div>
      )}

      {/* Low stock alerts */}
      {(() => {
        const lowItems = inventory.filter(inv => {
          const total = inv.vials_on_hand * inv.doses_per_vial;
          const remaining = Math.max(0, total - (inv.doses_used || 0));
          return remaining <= inv.reorder_threshold;
        });
        if (lowItems.length === 0) return null;
        return (
          <div style={{ marginBottom: 24 }}>
            {lowItems.map(inv => {
              const total = inv.vials_on_hand * inv.doses_per_vial;
              const remaining = Math.max(0, total - (inv.doses_used || 0));
              const isEmpty = remaining === 0;
              return (
                <div key={inv.peptide_id} onClick={() => setActive("inventory")} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 18px", marginBottom: 8, borderRadius: 14, cursor: "pointer",
                  background: isEmpty ? "#dc262215" : S.accent2Light,
                  border: `1px solid ${isEmpty ? "#dc262633" : S.accent2 + "33"}`,
                }}>
                  <span style={{ fontSize: "1rem" }}>{isEmpty ? "🚨" : "⚠️"}</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: "0.85rem", color: S.ink }}>{inv.peptide_name}</span>
                    <span style={{ fontSize: "0.78rem", color: isEmpty ? "#dc2626" : S.accent2, marginLeft: 8 }}>
                      {isEmpty ? "Out of stock" : `${remaining} doses left`}
                    </span>
                  </div>
                  <span style={{ fontSize: "0.72rem", color: S.muted }}>View →</span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Adherence ring + quick log */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }} className="tmp-dash-top">
        {/* Adherence ring */}
        <Card style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ position: "relative", width: 120, height: 120, flexShrink: 0 }}>
            <svg width="120" height="120" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="52" fill="none" stroke={S.surface2} strokeWidth="12" />
              <circle cx="60" cy="60" r="52" fill="none" stroke={adherence >= 80 ? S.accent : S.accent2} strokeWidth="12" strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 52}`} strokeDashoffset={`${2 * Math.PI * 52 * (1 - adherence / 100)}`}
                transform="rotate(-90 60 60)" style={{ transition: "stroke-dashoffset 0.6s ease" }} />
            </svg>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <div style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 800, color: S.ink, lineHeight: 1 }}>{adherence}%</div>
              <div style={{ fontSize: "0.6rem", color: S.muted, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", marginTop: 2 }}>Adherence</div>
            </div>
          </div>
          <div>
            <div style={{ ...css.fontDisplay, fontWeight: 700, fontSize: "1.1rem", color: S.ink, marginBottom: 6 }}>
              {adherence >= 80 ? "you're showing up." : adherence > 0 ? "still showing up." : "today's a new page."}
            </div>
            <p style={{ fontSize: "0.82rem", color: S.muted, lineHeight: 1.6, margin: 0 }}>{checked.length} of {protocol.length} doses logged today. Day by day, it adds up.</p>
          </div>
        </Card>

        {/* Quick Log */}
        <Card>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: S.muted, marginBottom: 16 }}>Quick Log</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
            {[
              { icon: "💉", label: "Dose", screen: "track" },
              { icon: "📊", label: "Biomarker", screen: "biomarkers" },
              { icon: "🤖", label: "Ask AI", screen: "ai" },
              { icon: "💉", label: "Add", screen: "protocol" },
            ].map((q, i) => (
              <button key={i} onClick={() => setActive(q.screen)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "16px 8px", background: S.bg, border: `1px solid ${S.border}`, borderRadius: 14, cursor: "pointer", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = S.accent + "66"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = S.border; }}>
                <span style={{ fontSize: "1.4rem" }}>{q.icon}</span>
                <span style={{ fontSize: "0.72rem", fontWeight: 600, color: S.ink2 }}>{q.label}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }} className="tmp-dash-top">
        {/* Week view */}
        <Card>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: S.muted, marginBottom: 16 }}>This Week</div>
          <div style={{ display: "flex", gap: 8 }}>
            {days.map((d, i) => {
              const isDone = i < todayIdx && protocol.length > 0;
              const isToday = i === todayIdx;
              const isFuture = i > todayIdx;
              return (
                <div key={d} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.06em", color: S.muted, textTransform: "uppercase", marginBottom: 8 }}>{d}</div>
                  <div style={{ width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", fontSize: "0.75rem", fontWeight: 700, background: isToday ? S.accent2 : isDone ? S.accent : isFuture ? S.border : S.surface2, color: isToday || isDone ? "#fff" : S.muted, boxShadow: isToday ? "0 4px 12px rgba(231,111,59,0.35)" : "none" }}>
                    {isToday ? "→" : isDone ? "✓" : isFuture ? "–" : "✕"}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Quick actions */}
        <Card>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: S.muted, marginBottom: 16 }}>Quick Actions</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "✅ Log today's doses", screen: "track" },
              { label: "💉 Add peptide to protocol", screen: "protocol" },
              { label: "📊 Log a biomarker", screen: "biomarkers" },
              { label: "🤖 Ask AI a question", screen: "ai" },
            ].map(a => (
              <button key={a.label} onClick={() => setActive(a.screen)} style={{ padding: "12px 16px", border: `1px solid ${S.border}`, borderRadius: 12, background: S.bg, fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", color: S.ink2, cursor: "pointer", textAlign: "left", transition: "all 0.2s", fontWeight: 500 }}>
                {a.label}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Today's peptides preview */}
      {protocol.length > 0 && (
        <Card style={{ marginTop: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: S.muted }}>Today's Protocol</div>
            <button onClick={() => setActive("track")} style={{ fontSize: "0.78rem", color: S.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Track Now →</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {protocol.slice(0, 3).map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: S.bg, borderRadius: 12, border: `1px solid ${S.border}` }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
                <div style={{ fontWeight: 700, fontSize: "0.88rem", flex: 1, color: S.accent2 }}>{p.name}</div>
                <div style={{ fontSize: "0.75rem", color: S.muted }}>{p.dose} · {p.route}</div>
                <div style={{ width: 26, height: 26, borderRadius: 8, border: `2px solid ${todayLog[p.id] ? S.accent : S.border}`, background: todayLog[p.id] ? S.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.7rem" }}>
                  {todayLog[p.id] ? "✓" : ""}
                </div>
              </div>
            ))}
            {protocol.length > 3 && <p style={{ fontSize: "0.78rem", color: S.muted, textAlign: "center", margin: 0 }}>+{protocol.length - 3} more peptides</p>}
          </div>
        </Card>
      )}

      {protocol.length === 0 && (
        <Card style={{ marginTop: 24, padding: 36, background: "linear-gradient(135deg, #19211d, #16271f)" }}>
          <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: "2.6rem", opacity: 0.9 }}>🌿</div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ ...css.fontDisplay, fontSize: "1.5rem", fontWeight: 500, fontStyle: "italic", color: S.ink, marginBottom: 8 }}>Here's your dashboard.</div>
              <p style={{ color: S.muted, fontSize: "0.92rem", lineHeight: 1.65, marginBottom: 18, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>
                All in one place from here. Add your first peptide whenever you're ready — nothing rushes you.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
                {[
                  { num: "1", title: "Browse Library", desc: "Explore 32+ peptides", action: () => setActive("library") },
                  { num: "2", title: "Build Protocol", desc: "Add your stack", action: () => setActive("protocol") },
                  { num: "3", title: "Track Daily", desc: "Log doses & biomarkers", action: () => setActive("track") },
                ].map(s => (
                  <div key={s.num} onClick={s.action} style={{ background: S.white, border: "1px solid " + S.border, borderRadius: 12, padding: "14px 16px", cursor: "pointer", transition: "all 0.2s" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: S.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 600, fontFamily: "'Fraunces', serif" }}>{s.num}</div>
                      <div style={{ fontWeight: 600, fontSize: "0.88rem", color: S.ink }}>{s.title}</div>
                    </div>
                    <div style={{ fontSize: "0.72rem", color: S.muted, marginLeft: 32 }}>{s.desc}</div>
                  </div>
                ))}
              </div>
              <BtnPrimary onClick={() => setActive("protocol")}>Start with your first peptide →</BtnPrimary>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── PROTOCOL BUILDER ────────────────────────────────────────
// Generate an .ics calendar file with recurring dose reminders

export { Dashboard };
